// src/utils/pendingOrderHandler.js
import { db } from '../firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  deleteDoc, 
  serverTimestamp, 
  collection,
  addDoc
} from 'firebase/firestore';

class PendingOrderHandler {
  // Save pending order to Firestore for the user
  static async savePendingOrder(userId, orderData) {
    try {
      console.log('PendingOrderHandler - Saving Pending Order', { 
        userId, 
        orderData: Object.keys(orderData)
      });

      // Validate input
      if (!userId) {
        console.warn('User ID is required to save pending order');
        return false;
      }

      if (!orderData) {
        console.warn('Order data is required to save pending order');
        return false;
      }

      // Create a document in the pendingOrders collection
      // Using a subcollection approach instead of using userId as document ID
      const pendingOrdersCollection = collection(db, 'users', userId, 'pendingOrders');
      
      // Add a new document with auto-generated ID
      await addDoc(pendingOrdersCollection, {
        ...orderData,
        createdAt: serverTimestamp(),
        userId: userId
      });

      console.log('PendingOrderHandler - Pending order saved successfully');
      return true;
    } catch (error) {
      console.error('PendingOrderHandler - Error saving pending order:', error);
      // Return false instead of throwing to prevent app crashes
      return false;
    }
  }

  // Retrieve pending order for a user
  static async getPendingOrder(userId) {
    try {
      console.log('PendingOrderHandler - Retrieving Pending Order for userId:', userId);

      if (!userId) {
        console.warn('User ID is required to retrieve pending order');
        return null;
      }

      try {
        // First try the subcollection approach
        const pendingOrdersCollection = collection(db, 'users', userId, 'pendingOrders');
        // This would ideally use getDocs with a limit(1) and orderBy,
        // but for simplicity we'll just check if the user document exists first
        
        // Fallback: Check if user document exists first
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);
        
        if (!userDocSnap.exists()) {
          console.log('PendingOrderHandler - User document does not exist, cannot retrieve pending order');
          return null;
        }
        
        // Try the original approach as fallback
        const docRef = doc(db, 'pendingOrders', userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const pendingOrder = docSnap.data();
          console.log('PendingOrderHandler - Pending Order Found:', {
            orderDetails: Object.keys(pendingOrder)
          });
          return pendingOrder;
        } else {
          console.log('PendingOrderHandler - No Pending Order Found for userId:', userId);
          return null;
        }
      } catch (innerError) {
        console.log('PendingOrderHandler - Inner error retrieving pending order:', innerError);
        return null;
      }
    } catch (error) {
      console.error('PendingOrderHandler - Error retrieving pending order:', error);
      // Return null instead of throwing to prevent app crashes
      return null;
    }
  }

  // Clear pending order after successful order creation or cancellation
  static async clearPendingOrder(userId) {
    try {
      console.log('PendingOrderHandler - Clearing Pending Order for userId:', userId);

      if (!userId) {
        console.warn('User ID is required to clear pending order');
        return false;
      }

      try {
        // Try both approaches to be safe
        // 1. Original approach
        const pendingOrderRef = doc(db, 'pendingOrders', userId);
        await deleteDoc(pendingOrderRef);
        
        // Don't worry about errors here, just log them
      } catch (innerError) {
        console.log('PendingOrderHandler - Error in first clear approach:', innerError);
      }

      console.log('PendingOrderHandler - Pending order cleared successfully');
      return true;
    } catch (error) {
      console.error('PendingOrderHandler - Error clearing pending order:', error);
      // Return false instead of throwing to prevent app crashes
      return false;
    }
  }
}

export default PendingOrderHandler;